<template>
  <div
    class="bg-gray-50 dark:bg-gray-900 flex items-center justify-center flex-grow py-12 px-4 sm:px-6 lg:px-8"
  >
    <div class="max-w-md w-full space-y-8">
      <div>
        <h2 class="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
          Sign in to your account
        </h2>
        <p
          v-if="!checkingRegistration && registrationEnabled"
          class="mt-2 text-center text-sm text-gray-600 dark:text-gray-400"
        >
          Or
          <router-link
            to="/register"
            class="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
          >
            create a new account
          </router-link>
        </p>
      </div>

      <form class="mt-8 space-y-6" @submit.prevent="handleLogin">
        <div class="rounded-md shadow-sm -space-y-px">
          <div>
            <label for="email" class="sr-only">Email address</label>
            <input
              id="email"
              v-model="form.email"
              name="email"
              type="email"
              autocomplete="email"
              required
              class="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-800 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
              placeholder="Email address"
            />
          </div>
          <div>
            <label for="password" class="sr-only">Password</label>
            <input
              id="password"
              v-model="form.password"
              name="password"
              type="password"
              autocomplete="current-password"
              required
              class="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-800 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
              placeholder="Password"
            />
          </div>
        </div>

        <div v-if="error" class="text-red-600 dark:text-red-400 text-sm text-center">
          {{ error }}
        </div>

        <div class="flex items-center justify-between">
          <div class="text-sm">
            <router-link
              to="/forgot-password"
              class="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
            >
              Forgot your password?
            </router-link>
          </div>
        </div>

        <div>
          <Button
            type="submit"
            :disabled="loading"
            :label="loading ? 'Signing in...' : 'Sign in'"
            severity="primary"
            :loading="loading"
            class="w-full"
          />
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "../../stores/auth";
import api from "../../services/api";
import { getHelperLivePath } from "../../utils/eventStatus";
import Button from "primevue/button";

export default {
  name: "Login",
  components: {
    Button
  },
  setup() {
    const router = useRouter();
    const authStore = useAuthStore();

    const form = ref({
      email: "",
      password: ""
    });
    const error = ref("");
    const loading = ref(false);
    const registrationEnabled = ref(false); // Start as false, update after check
    const checkingRegistration = ref(true); // Track loading state

    const checkRegistrationStatus = async () => {
      try {
        checkingRegistration.value = true;
        const response = await api.get("/api/settings/registration/public");
        console.log("Registration status response:", response.data);
        if (response.data.success) {
          registrationEnabled.value = response.data.data.registrationEnabled;
          console.log("Registration enabled:", registrationEnabled.value);
        } else {
          console.warn("Registration status check failed:", response.data.message);
          registrationEnabled.value = true; // Default to enabled if check fails
        }
      } catch (err) {
        // If we can't check the status, default to enabled (show link)
        console.error("Could not check registration status:", err);
        console.error("Error details:", err.response?.data || err.message);
        registrationEnabled.value = true;
      } finally {
        checkingRegistration.value = false;
      }
    };

    const handleLogin = async () => {
      try {
        loading.value = true;
        error.value = "";

        console.log("Attempting login with:", form.value);
        const result = await authStore.login(form.value);
        console.log("Login result:", result);

        if (result.success) {
          // Check if user must change password
          if (result.mustChangePassword) {
            // Stay on login page - force password change modal will be shown in App.vue
            // The modal will be triggered by checking authStore.user.mustChangePassword
          } else {
            // Redirect helpers to Live page, others to dashboard
            if (authStore.user?.role === "helper") {
              try {
                const response = await api.get("/api/events");
                if (response.data.success) {
                  const events = response.data.data.events || [];
                  router.push(getHelperLivePath(events));
                } else {
                  router.push("/events/live");
                }
              } catch {
                router.push("/events/live");
              }
            } else {
              router.push("/");
            }
          }
        } else {
          error.value = result.message || "Login failed";
        }
      } catch (err) {
        console.error("Login error:", err);
        error.value = "An unexpected error occurred";
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      checkRegistrationStatus();
    });

    return {
      form,
      error,
      loading,
      registrationEnabled,
      checkingRegistration,
      handleLogin
    };
  }
};
</script>
